import { useRef, useState } from 'react'
import type { DragEvent } from 'react'
import { Button, LayerCard, Text, cn } from '@cloudflare/kumo'
import { FilePdfIcon } from '@phosphor-icons/react'
import { useTranslation } from '../../i18n'
import { PrintByEmail } from './PrintByEmail'

interface Props {
  onFiles: (files: File[]) => void
  disabled?: boolean
  /** Queue already has jobs: the button becomes a secondary "Add another PDF". */
  hasJobs: boolean
}

const ACCEPT = '.pdf,application/pdf'

/**
 * "Send" card. A big button opens the file picker (the only affordance on
 * phones). The card itself is also a drop target for pointer devices; the
 * "drag here" hint is shown from md up. Size/MIME validation stays server-side.
 */
export function PrintUploadCard({ onFiles, disabled, hasJobs }: Props) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    if (disabled) return
    const files = Array.from(e.dataTransfer.files ?? [])
    if (files.length > 0) onFiles(files)
  }

  return (
    <div
      data-testid="print-drop-zone"
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <LayerCard className={cn(dragging && 'ring-2 ring-kumo-focus')}>
        <LayerCard.Primary>
          <div className="flex flex-col gap-3">
            <Button
              variant={hasJobs ? 'secondary' : 'primary'}
              size="lg"
              className="w-full"
              style={{ minHeight: 48 }}
              icon={<FilePdfIcon size={20} />}
              disabled={disabled}
              loading={disabled}
              type="button"
              onClick={() => inputRef.current?.click()}
            >
              {hasJobs ? t('print.send.addAnother') : t('print.send.choose')}
            </Button>
            <Text variant="secondary" DANGEROUS_className="text-center">
              {t('print.send.caption')}
            </Text>
            <span className="hidden text-center text-sm text-kumo-subtle md:block">
              {t('print.send.dragHint')}
            </span>
          </div>
          <div className="mt-4 border-t border-kumo-line" style={{ paddingTop: 16 }}>
            <PrintByEmail />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            data-testid="print-file-input"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length > 0) onFiles(files)
              e.target.value = ''
            }}
          />
        </LayerCard.Primary>
      </LayerCard>
    </div>
  )
}
